import type { Dispatch } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'
import qs from 'qs'

import { API_BASE_URL } from 'src/common/config'

import { version } from '../../../package.json'

import { resetAuthStore, setAuthInfo } from '@v2/domains/auth/store/auth.store'
import { isCookieAuthenticationEnabled } from '@v2/domains/auth/utils/isCookieAuthenticationEnabled'
import { destroyUILocalStore } from '@v2/domains/ui/store/ui.store'
import { resetUserSessionStore } from '@v2/domains/user/store/user.store'

const ignoreUrls = ['/v3/auth/login']
const authEndpointsWithoutRefresh = [
  '/v3/auth/login',
  '/v3/auth/refresh',
  '/v3/auth/forgot-password',
  '/v3/auth/magic-link/login',
  '/auth/logout',
]

interface CookieRetryConfig {
  _cookieAuthRetried?: boolean
}

interface ApiStoreAccess {
  dispatch: Dispatch
  getState: () => {
    auth: {
      accessToken?: string
      organizationId?: string
      membershipId?: string
    }
  }
}

let storeAccess: ApiStoreAccess | undefined
let cookieRefreshPromise: Promise<void> | null = null

export const configureApiStore = (store: ApiStoreAccess) => {
  storeAccess = store
}

const getApiStore = () => {
  if (!storeAccess) {
    throw new Error('API store access attempted before configuration')
  }

  return storeAccess
}

const api = axios.create({
  baseURL: API_BASE_URL,
  // Required for the cookie-authentication rollout. It is harmless while the
  // legacy bearer-token flow remains enabled.
  withCredentials: true,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  paramsSerializer: (params: any) =>
    qs.stringify(params, { arrayFormat: 'repeat' }),
})

const clearClientSession = () => {
  const store = getApiStore()
  store.dispatch(resetAuthStore())
  store.dispatch(resetUserSessionStore())
  store.dispatch(destroyUILocalStore())
}

const redirectToLogin = () => {
  if (window.location.pathname !== '/login') {
    window.location.replace('/login')
  }
}

const requestCookieSessionRefresh = async () => {
  const store = getApiStore()
  const { organizationId, membershipId } = store.getState().auth
  const response = await axios.post<{ userId?: string }>(
    `${API_BASE_URL}/v3/auth/refresh`,
    {
      returnType: 'COOKIES',
      ...(organizationId && { organizationId }),
      ...(membershipId && { membershipId }),
    },
    { withCredentials: true },
  )

  store.dispatch(
    setAuthInfo({
      userId: response.data.userId,
      ...(organizationId && { organizationId }),
      ...(membershipId && { membershipId }),
    }),
  )
}

export const refreshCookieSession = () => {
  if (!cookieRefreshPromise) {
    cookieRefreshPromise = requestCookieSessionRefresh()
      .catch((error) => {
        const status = (error as AxiosError | undefined)?.response?.status
        // An outage does not establish that the refresh cookie is invalid.
        if (status === 401 || status === 403) {
          clearClientSession()
          redirectToLogin()
        }
        throw error
      })
      .finally(() => {
        cookieRefreshPromise = null
      })
  }

  return cookieRefreshPromise
}

api.interceptors.response.use(
  async (res) => {
    return res
  },
  async (error) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error)
    }

    if (!isCookieAuthenticationEnabled) {
      clearClientSession()
      window.location.replace('/')
      return Promise.reject(error)
    }

    const config = error.config as CookieRetryConfig & {
      url?: string
    }
    const shouldNotRefresh = authEndpointsWithoutRefresh.some((path) =>
      config?.url?.includes(path),
    )

    if (!config || shouldNotRefresh) {
      if (config?.url?.includes('/v3/auth/refresh')) {
        clearClientSession()
        redirectToLogin()
      }
      return Promise.reject(error)
    }

    if (config._cookieAuthRetried) {
      const isProfileRequest = config.url?.includes('/v3/auth/profile')

      if (isProfileRequest) {
        clearClientSession()
        redirectToLogin()
      }

      return Promise.reject(error)
    }

    config._cookieAuthRetried = true

    try {
      await refreshCookieSession()
      return api(config)
    } catch (refreshError) {
      // Preserve the refresh failure status so callers can distinguish an
      // unavailable backend from a rejected session.
      return Promise.reject(refreshError)
    }
  },
)

api.interceptors.request.use(async (config) => {
  const store = getApiStore()
  const session = store.getState().auth
  if (ignoreUrls.includes(config.url ?? '')) {
    return config
  }

  if (config.headers) {
    if (!isCookieAuthenticationEnabled) {
      config.headers.Authorization = `Bearer ${session?.accessToken}`
    }
    config.headers.product = `W360`
    config.headers['app-platform'] = `web`
    config.headers['app-version'] = version
    config.headers['app-env'] = 'TBD'
  }
  return config
})

export default api
